import type { ReactElement, ReactNode } from "react";

import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";

import { CraftedSignalStrategyContainer } from "../containers/CraftedSignalStrategy/CraftedSignalStrategyContainer";
import { CustomErrorStrategyContainer } from "../containers/CustomErrorStrategy/CustomErrorStrategyContainer";
import { FiltersStateStrategyContainer } from "../containers/FiltersStateStrategy";
import { SuggestedQueriesContainer } from "../containers/SuggestedQueriesStrategy";
import type {
    EnrichedSignalsContentJsonObject,
    EnrichedExistingFilterSet,
    EnrichedFiltersContentJsonObject,
    EnrichedCraftedSignal,
    ErrorJsonObject,
    SuggestedQueries,
} from "../types/content";
import type { MdComponentProps } from "../types/mdComponentProps";

import { buildFiltersStateSettings } from "./buildFiltersStateSettings";

export interface IComponentFactoryContext {
    messageId: string;
    componentProps: MdComponentProps<LeadsSearchAllFiltersTypes>;
    loader?: ReactNode;
}

export const createSignalsFiltersComponent = (
    jsonObj: EnrichedSignalsContentJsonObject,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps } = context;

    return (
        <FiltersStateStrategyContainer
            key={`${messageId}-${keyIdentifier}`}
            settings={buildFiltersStateSettings(jsonObj.name)}
            filtersStateData={jsonObj.data}
            filtersManager={componentProps.filtersManager}
            onFilterUpdate={componentProps.onFilterUpdate}
            onFilterValueRemove={componentProps.onFilterValueRemove}
        />
    );
};

export const createExistingFilterSetComponent = (
    jsonObj: EnrichedExistingFilterSet,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps } = context;

    return (
        <FiltersStateStrategyContainer
            key={`${messageId}-${keyIdentifier}`}
            settings={buildFiltersStateSettings(jsonObj.name)}
            filtersStateData={jsonObj.data}
            filtersManager={componentProps.filtersManager}
            onFilterUpdate={componentProps.onFilterUpdate}
            onFilterValueRemove={componentProps.onFilterValueRemove}
        />
    );
};

export const createFiltersComponent = (
    jsonObj: EnrichedFiltersContentJsonObject,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps } = context;

    return (
        <FiltersStateStrategyContainer
            key={`${messageId}-${keyIdentifier}`}
            settings={buildFiltersStateSettings(jsonObj.name)}
            filtersStateData={jsonObj.data}
            filtersManager={componentProps.filtersManager}
            onFilterUpdate={componentProps.onFilterUpdate}
            onFilterValueRemove={componentProps.onFilterValueRemove}
        />
    );
};

export const createCraftedSignalComponent = (
    jsonObj: EnrichedCraftedSignal,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps, loader } = context;

    return (
        <CraftedSignalStrategyContainer
            key={`${messageId}-${keyIdentifier}`}
            messageId={messageId}
            loader={loader}
            settings={buildFiltersStateSettings(jsonObj.name)}
            filtersManager={componentProps.filtersManager}
            onFilterUpdate={componentProps.onFilterUpdate}
            onFilterValueRemove={componentProps.onFilterValueRemove}
        />
    );
};

export const createSuggestedQueriesComponent = (
    jsonObj: SuggestedQueries,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps } = context;

    return (
        <SuggestedQueriesContainer
            key={`${messageId}-${keyIdentifier}`}
            suggestions={jsonObj.data}
            onSuggestionClick={componentProps.onMessageSubmit}
        />
    );
};

export const createCustomErrorComponent = (
    jsonObj: ErrorJsonObject,
    keyIdentifier: string | number,
    context: IComponentFactoryContext,
): ReactElement => {
    const { messageId, componentProps } = context;

    return (
        <CustomErrorStrategyContainer
            key={`${messageId}-${keyIdentifier}`}
            messageId={messageId}
            code={jsonObj.code}
            onRetry={componentProps.onMessageSubmit}
        />
    );
};
